import { PdfTemplateFieldMapping, PdfTemplateRow } from "@/data/models/ClientAdministrationRow";
import { isLinked, mappingsFromTemplateFields, mappingsKey, PdfTemplateEditorRow } from "@/utils/PdfTemplateMapping";

/**
 * PDF template files in the admin portal (PDF-Vorlagen): the file-name rules, how IRES Project reads a
 * name, which template the HyDry certificate uses, and taking over another template's mapping. Pure.
 *
 * IRES Cloud repeats the name rules and also refuses a name any file in the scope's folder carries
 * (`requireFreeFileName`); the checks here only give the user feedback while typing.
 */

/** The largest PDF IRES Cloud accepts (`MAX_TEMPLATE_FILE_BYTES`). */
export const MAX_TEMPLATE_FILE_BYTES = 6 * 1024 * 1024;

/** IRES Cloud's limit for a template file name. */
export const MAX_TEMPLATE_FILE_NAME_LENGTH = 200;

/** The certificate's template is the first client template, by id, whose file name starts so (IRES Cloud). */
export const HYDRY_CERTIFICATE_PREFIX = "-HyDry";

export type TemplateFileNameProblem = "EMPTY" | "TOO_LONG" | "INVALID_CHARACTERS" | "TAKEN";

/**
 * The name as IRES Cloud stores it (trimmed, `.pdf` added), or why it cannot be one. Mirrors
 * `normaliseTemplateFileName`; `takenFileNames` are the scope's current templates' file names.
 */
export function checkTemplateFileName(
  input: string,
  takenFileNames: string[]
): { fileName: string } | { problem: TemplateFileNameProblem } {
  const trimmed = input.trim();

  if (trimmed === "" || trimmed.toLowerCase() === ".pdf") {
    return { problem: "EMPTY" };
  }

  const fileName = trimmed.toLowerCase().endsWith(".pdf") ? trimmed : `${trimmed}.pdf`;

  // eslint-disable-next-line no-control-regex
  if (/[/\\\u0000-\u001f\u007f]/.test(fileName) || fileName.includes("..")) {
    return { problem: "INVALID_CHARACTERS" };
  }
  if (fileName.length > MAX_TEMPLATE_FILE_NAME_LENGTH) {
    return { problem: "TOO_LONG" };
  }
  if (takenFileNames.some((taken) => taken.toLowerCase() === fileName.toLowerCase())) {
    return { problem: "TAKEN" };
  }

  return { fileName };
}

/** The work phase a name's `-L ` / `-S ` / `-T ` / `-W ` switch assigns in IRES Project. */
export type TemplatePhase = "LEAK_DETECTION" | "DAMAGE_ASSESSMENT" | "DRYING_CONCLUSION" | "REPAIR";

const PHASE_SWITCHES: Array<{ token: string; phase: TemplatePhase }> = [
  { token: "-l ", phase: "LEAK_DETECTION" },
  { token: "-s ", phase: "DAMAGE_ASSESSMENT" },
  { token: "-t ", phase: "DRYING_CONCLUSION" },
  { token: "-w ", phase: "REPAIR" },
];

/** What IRES Project makes of a template's name. */
export interface TemplateNameSwitches {
  /** `-HyDry…`: never offered in IRES Project's picker. */
  hiddenInPicker: boolean;
  /** No leading `-`: offered in every phase and for every insurer. */
  alwaysShown: boolean;
  phase: TemplatePhase | null;
  /** The insurer word the project's insurer must contain, lower case, or null. */
  insurer: string | null;
  /** `+Bild`: the report adds the phase's images to this document. */
  withImages: boolean;
}

/**
 * How IRES Project reads a template's name (its `dev` branch, 2026-10-05): the picker hides `-HyDry`
 * names and always shows names without a leading `-`; otherwise the first phase switch found anywhere
 * (`-L ` before `-S ` before `-T ` before `-W `), and the first `-word ` longer than one letter as the
 * insurer; `+Bild` anywhere adds images. Shown so the author sees what a name will do.
 */
export function templateNameSwitches(fileName: string): TemplateNameSwitches {
  const name = fileName.trim().toLowerCase();
  const words: string[] = [];
  const pattern = /-(\S+)\s/g;
  for (let match = pattern.exec(name); match !== null; match = pattern.exec(name)) {
    words.push(match[1]);
  }
  const insurer = words.find((word) => word.length > 1)?.replace(/^-/, "") ?? null;

  return {
    hiddenInPicker: name.startsWith(HYDRY_CERTIFICATE_PREFIX.toLowerCase()),
    alwaysShown: !name.startsWith("-"),
    phase: PHASE_SWITCHES.find((entry) => name.includes(entry.token))?.phase ?? null,
    insurer,
    withImages: name.includes("+bild"),
  };
}

/**
 * The template the HyDry certificate uses: the first, by id, whose file name starts with `-HyDry`
 * (case as IRES Cloud compares it). Null for none, and for the shared templates, which it never uses.
 */
export function certificateTemplateId(
  templates: Array<{ id: string; fileName: string | null }>,
  isShared: boolean
): string | null {
  if (isShared) {
    return null;
  }

  return (
    templates
      .filter((template) => (template.fileName ?? "").startsWith(HYDRY_CERTIFICATE_PREFIX))
      .map((template) => template.id)
      .sort()[0] ?? null
  );
}

/** A template whose mapping can be taken over, as the import dialog lists it. */
export interface MappingImportSource {
  /** Unique across both groups (a copy may share its id with its source). */
  id: string;
  label: string;
  group: "own" | "shared";
  mappings: PdfTemplateFieldMapping[];
}

/** The templates to take a mapping from: the scope's others, then the shared ones (none for shared). */
export function mappingImportSources(
  own: PdfTemplateRow[],
  shared: PdfTemplateRow[],
  currentTemplateId: string
): MappingImportSource[] {
  const toSource = (group: "own" | "shared") => (row: PdfTemplateRow): MappingImportSource => ({
    id: `${group}:${row.id}`,
    label: row.name || row.fileName || row.id,
    group,
    mappings: mappingsFromTemplateFields(row.fields),
  });

  return [...own.filter((row) => row.id !== currentTemplateId).map(toSource("own")), ...shared.map(toSource("shared"))];
}

/** What taking over another template's mapping would do, by field name. */
export interface MappingImport {
  /** Fields of this PDF with nothing linked that the source links. */
  fresh: string[];
  /** Fields linked here differently than in the source: overwritten only when chosen. */
  conflicting: string[];
  /** Fields linked here exactly as in the source. */
  unchanged: string[];
  /** Source fields this PDF does not have: ignored, as IRES Admin ignores them. */
  missing: string[];
}

/**
 * Compares a source template's mapping with this template's rows. Only fields this PDF has count;
 * when its fields could not be read, the fields it already maps.
 */
export function compareMappingImport(rows: PdfTemplateEditorRow[], source: PdfTemplateFieldMapping[]): MappingImport {
  const targets = new Map(rows.filter((row) => row.inPdf !== false).map((row) => [row.fieldName, row]));
  const result: MappingImport = { fresh: [], conflicting: [], unchanged: [], missing: [] };

  for (const mapping of source.filter((entry) => entry.values.length > 0)) {
    const row = targets.get(mapping.fieldName);

    if (!row) {
      result.missing.push(mapping.fieldName);
    } else if (!isLinked(row)) {
      result.fresh.push(mapping.fieldName);
    } else if (mappingsKey([row.mapping as PdfTemplateFieldMapping]) === mappingsKey([mapping])) {
      result.unchanged.push(mapping.fieldName);
    } else {
      result.conflicting.push(mapping.fieldName);
    }
  }

  return result;
}

/**
 * The rows with the source's mapping taken over for every fresh field and the chosen conflicting
 * ones. Taken-over texts get no id: they are new to this template.
 */
export function applyMappingImport(
  rows: PdfTemplateEditorRow[],
  source: PdfTemplateFieldMapping[],
  overwrite: string[]
): PdfTemplateEditorRow[] {
  const plan = compareMappingImport(rows, source);
  const take = new Set([...plan.fresh, ...plan.conflicting.filter((name) => overwrite.includes(name))]);
  const bySource = new Map(source.map((mapping) => [mapping.fieldName, mapping]));

  return rows.map((row) => {
    const mapping = take.has(row.fieldName) ? bySource.get(row.fieldName) : undefined;
    return mapping
      ? {
          ...row,
          mapping: {
            ...mapping,
            values: [...mapping.values],
            transformations: mapping.transformations.map((transformation) => ({ ...transformation, id: null })),
          },
        }
      : row;
  });
}

/** A file operation on a template, as the dialogs and confirmations name it. */
export type PdfTemplateFileAction = "upload" | "replace" | "rename" | "copyToShared" | "copyFromShared" | "delete";

/** Whose template it is, for the consequence lines. */
export interface PdfTemplateFileScope {
  isShared: boolean;
  /** The client's name (or id); unused for a shared template. */
  clientName: string;
  /** Whether this template is the one the client's HyDry certificate uses. */
  isCertificateTemplate: boolean;
  /** The template's name, for a delete. */
  templateName: string;
}

/**
 * What an operation reaches, one i18n sentence a line, shown before it runs. A client's template
 * reaches the client's new PDFs from IRES Project; a shared one only later take-overs, because every
 * client that took it over works on its own copy. A replace says why the file gets a new name.
 */
export function fileActionConsequences(
  action: PdfTemplateFileAction,
  scope: PdfTemplateFileScope
): Array<{ key: string; params: string[] }> {
  const line = (key: string, params: string[] = []) => ({ key: `admin.pdfTemplates.files.consequences.${key}`, params });
  const byScope = (key: string) => (scope.isShared ? line(`${key}Shared`) : line(`${key}Client`, [scope.clientName]));
  const certificate = (key: string) => (!scope.isShared && scope.isCertificateTemplate ? [line(key)] : []);

  switch (action) {
    case "upload":
      return [byScope("upload")];
    case "replace":
      return [byScope("replace"), line("replaceFile"), ...certificate("certificateReplace")];
    case "rename":
      return [byScope("rename"), ...certificate("certificateRename")];
    case "copyToShared":
      return [line("copyToShared", [scope.clientName])];
    case "copyFromShared":
      return [line("copyFromShared", [scope.clientName])];
    case "delete":
      return [
        scope.isShared
          ? line("deleteShared", [scope.templateName])
          : line("deleteClient", [scope.templateName, scope.clientName]),
        line("deleteKeepsFile"),
      ];
  }
}

/**
 * What deleting a template does to the client's HyDry certificate: nothing, another template takes
 * over (`CHANGED`), or none is left (`LOST`).
 */
export function certificateAfterDelete(
  templates: Array<{ id: string; fileName: string | null }>,
  deletedId: string,
  isShared: boolean
): "UNCHANGED" | "CHANGED" | "LOST" {
  const before = certificateTemplateId(templates, isShared);
  if (before !== deletedId) {
    return "UNCHANGED";
  }
  return certificateTemplateId(
    templates.filter((template) => template.id !== deletedId),
    isShared
  ) === null
    ? "LOST"
    : "CHANGED";
}

/** The snackbar message for a failed file operation, by IRES Cloud's refusal. */
export function fileOperationErrorKey(error: unknown): string {
  const failure = (error ?? {}) as { code?: unknown; details?: { reason?: unknown } | null };
  const code = typeof failure.code === "string" ? failure.code.replace(/^functions\//, "") : "";
  const reason = typeof failure.details?.reason === "string" ? failure.details.reason : "";

  if (code === "aborted") {
    return "admin.pdfTemplates.edit.changedMeanwhile";
  }

  const known = [
    "FILE_NAME_TAKEN",
    "FILE_TOO_LARGE",
    "NOT_A_PDF",
    "ENCRYPTED",
    "NO_FILE",
    "FILE_NAME_EMPTY",
    "FILE_NAME_TOO_LONG",
    "FILE_NAME_INVALID_CHARACTERS",
  ];
  return known.includes(reason) ? `admin.pdfTemplates.files.errors.${reason}` : "admin.pdfTemplates.files.errors.FAILED";
}

/** A picked file's content as base64 (without the data-URL prefix), for an IRES Cloud upload. */
export function readFileAsBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? "");
      resolve(result.substring(result.indexOf(",") + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** A PDF returned as base64 by IRES Cloud, as a blob a tab can show. */
export function pdfBlobFromBase64(contentBase64: string): Blob {
  const bytes = Uint8Array.from(atob(contentBase64), (character) => character.charCodeAt(0));
  return new Blob([bytes], { type: "application/pdf" });
}
